import { forwardRef, useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useSession } from '../layout/TopBar';
import { collectionVis, copiedCollectionMessage, duplicatedMessage, placeStudies, shortDate, studyName, studyThumbnail, useCollection, useCollectionMutations, useWritableTargets, type CollectionDetail, type PlacedStudy, type StudyGroup, useShareLinks } from '../api/collections';
import { routeBase, useOpenStudy } from '../api/collections';
import { orderTargetWorkspaces, workspaceOfCollection, type CollectionTarget } from '../../features/collections/api';
import { useSelectedWorkspace } from '../../stores/workspaceStore';
import { Avatar } from '../ui/Avatar';
import { BrowseBar } from '../ui/BrowseBar';
import { Button, TextLink } from '../ui/Button';
import { Cover, DxBadge, StackMini, StudyCard } from '../ui/Cards';
import { CommentThread } from '../ui/CommentThread';
import { confirm } from '../ui/Confirm';
import { nouns } from '../ui/text';
import { Checkbox } from '../ui/Controls';
import { IconChevronDown, IconChevronUp, IconComment, IconCopy, IconHeart, IconPlus, IconTrash, IconUpload } from '../ui/icons';
import { MenuAnchor, MenuItem, MenuLabel, MenuSurface, useDismiss } from '../ui/Menu';
import { InlineText, InlineTitle } from '../ui/Inline';
import { RichText } from '../ui/RichText';
import { SelectionToolbar, type Target } from '../ui/SelectionToolbar';
import { toast } from '../ui/toast';
import { OverVisibleWarning, VIS_LABEL, VisChip, VisIcon, fromServerVisibility, grantsMore, type OverItem, type Visibility } from '../ui/Visibility';
import { useBrowseParams } from './CollectionsPage';
import { toThread, useCommentMutations, useComments } from '../api/discussion';
import { useSlide, useStudyDrag, type DropTarget } from '@/components/drag/studyDrag';

/**
 * The collection page: sticky header (inline title, visibility chip, like,
 * Comment, Upload, ⋯), rich-text description, author and stats, Browse Bar,
 * then the group bands and the ungrouped "Studies" band, the selection
 * toolbar, and Discussion at the bottom.
 */
type Editing = null | { kind: 'title' } | { kind: 'gname'; id: string } | { kind: 'gdesc'; id: string };

/** The Move menu's "Ungroup" row: this collection, no group. Not a real target
 *  key, so it cannot collide with the collection's own row. */
const UNGROUP = 'ungroup';

/**
 * The list view's columns: select, image, Study, Description, Diagnosis,
 * Images, Visibility, Uploaded, Saves. Description and diagnosis have columns
 * of their own so a long collection can be read down them without opening a
 * study. Each stops at two lines, ending in an ellipsis.
 */
const LIST_COLUMNS = '28px 88px minmax(136px,0.5fr) minmax(0,1.2fr) minmax(0,1.2fr) 64px 92px 104px 56px';

interface Section {
  key: string;
  group: StudyGroup | null;
  all: PlacedStudy[];
  shown: PlacedStudy[];
}

type Placement = { ids: string[]; groupId: string | null; afterId?: string; beforeId?: string };

/**
 * A drop target as a placement: before the study it was dropped in front of,
 * or — dropped at the end of a band — right after the band's last study the
 * page SHOWS, so a drop under a filter lands where it was seen to.
 */
function placement(ids: string[], t: DropTarget, studies: PlacedStudy[], visible: (s: PlacedStudy) => boolean): Placement {
  const groupId = t.band === 'root' ? null : t.band;
  if (t.beforeId) return { ids, groupId, beforeId: t.beforeId };
  const moving = new Set(ids);
  const last = studies.filter((s) => s.group_public_id === groupId && !moving.has(s.public_id) && visible(s)).pop();
  return { ids, groupId, afterId: last?.public_id };
}

export function CollectionPage() {
  const { publicId } = useParams<{ publicId: string }>();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const { data: col } = useCollection(publicId);
  const { data: me } = useSession();
  const targets = useWritableTargets();
  const { current: selectedWorkspace } = useSelectedWorkspace(!!me?.userId);
  const m = useCollectionMutations();
  const links = useShareLinks();
  const visWrite = useRef<Promise<unknown>>(Promise.resolve());
  const { state: browse, set: setBrowse } = useBrowseParams();
  const thread = useComments('collection', publicId);
  const cm = useCommentMutations('collection', publicId);
  const openStudy = useOpenStudy();

  const [editing, setEditing] = useState<Editing>(params.get('edit') === 'title' ? { kind: 'title' } : null);
  const [aboutEditing, setAboutEditing] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);
  useDismiss(copyOpen, () => setCopyOpen(false));
  const [selected, setSelected] = useState<Record<string, true>>({});
  const [stuck, setStuck] = useState(false);
  const [newGroupId, setNewGroupId] = useState<string | null>(null);
  const isNew = params.get('new') === '1';
  // Drag and drop. The handlers need the collection, which is not loaded on
  // the first render, so the hook reaches them through a ref set further down.
  const dropApi = useRef<{ carry: (id: string) => string[]; drop: (ids: string[], t: DropTarget) => void }>({ carry: (id) => [id], drop: () => undefined });
  const { drag, onPointerDown, overlayRef } = useStudyDrag({
    enabled: col?.editable === true,
    carry: (id) => dropApi.current.carry(id),
    onDrop: (ids, t) => dropApi.current.drop(ids, t),
  });
  // A drop just made: shown until the collection itself says so, which is a
  // moment later (the cache write is async). Without it the studies flicked
  // back to where they came from for a frame before landing.
  const [settling, setSettling] = useState<Placement | null>(null);
  useEffect(() => setSettling(null), [col]);
  const bandsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => setStuck(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  useEffect(() => {
    if (params.get('edit')) {
      const p = new URLSearchParams(params);
      p.delete('edit');
      setParams(p, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Deep links: #discussion (from a notification) and #grp_… (from search)
  // scroll to their block once the collection has rendered.
  useEffect(() => {
    const hash = window.location.hash.slice(1);
    if (!col || !hash) return;
    const el = hash === 'discussion' ? document.querySelector('[data-disc]') : document.querySelector(`[data-grp="${CSS.escape(hash)}"]`);
    if (!el) return;
    window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 120, behavior: 'smooth' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!col, window.location.hash]);

  const meName = me?.displayName || me?.email || '';

  // ── derived ──
  const fl = browse.query.trim().toLowerCase();
  const anyFilter = !!fl || browse.tab === 'saved' || browse.share !== 'all';
  const matchShare = (v: Visibility) => browse.share === 'all' || browse.share === v;
  // A study's OWN setting on its badge, row and filter, the same as its own
  // chip shows: what the collection adds is said in the study's menu.
  const studyVis = (s: PlacedStudy): Visibility => fromServerVisibility(s.visibility_own);
  const matchStudy = (s: PlacedStudy) =>
    (!fl || studyName(s).toLowerCase().includes(fl) || (s.description ?? '').toLowerCase().includes(fl) || (s.diagnosis ?? '').toLowerCase().includes(fl)) &&
    (browse.tab !== 'saved' || s.liked) &&
    matchShare(studyVis(s));

  // While a drag is on, the page is laid out as if the studies had already
  // been dropped where the pointer is: that is the preview.
  const dragIds = drag?.ids;
  const dragTarget = drag?.target;
  const sections: Section[] = useMemo(() => {
    if (!col) return [];
    const moving = new Set(dragIds ?? []);
    const preview = dragIds && dragTarget ? placement(dragIds, dragTarget, col.studies, matchStudy) : settling;
    const studies = preview ? placeStudies(col.studies, preview) : col.studies;
    const mk = (g: StudyGroup | null): Section | null => {
      const all = studies.filter((s) => (g ? s.group_public_id === g.public_id : s.group_public_id === null));
      const groupMatches = !!g && !!fl && [g.name, g.description ?? ''].some((t) => t.toLowerCase().includes(fl));
      // The studies being carried are shown whatever the filter: they are
      // what the person is looking at.
      const shown = groupMatches
        ? all.filter((s) => moving.has(s.public_id) || ((browse.tab !== 'saved' || s.liked) && matchShare(studyVis(s))))
        : all.filter((s) => moving.has(s.public_id) || matchStudy(s));
      // An empty collection keeps its one section under any filter: it is
      // where "This collection is empty" and the way to upload live.
      if (anyFilter && shown.length === 0 && (g || col.studies.length || col.groups.length)) return null;
      return { key: g ? g.public_id : 'root', group: g, all, shown };
    };
    return [...col.groups.map(mk), mk(null)].filter((x): x is Section => !!x);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [col, fl, browse.tab, browse.share, dragIds, dragTarget, settling]);
  useSlide(bandsRef, sections.map((sec) => sec.key + ':' + sec.shown.map((s) => s.public_id).join(',')).join('|'), browse.view);

  if (!col) {
    return <div style={{ flex: 1 }} />;
  }
  /**
   * Whether this person may CHANGE this collection: an admin or editor of its
   * workspace, per the server (`editable` on GET /collections/:id). A viewer
   * reads it, saves it and joins its discussion, and is offered nothing else.
   *
   * `=== true` rather than a truthiness test, because the field is optional:
   * an older server, or a payload built by a path that does not set it, must
   * read as "no". Permission is granted, never merely unrefuted.
   *
   * The flag was already fetched and already correct; it reached exactly one
   * control (the visibility chip). Everything else on this page — rename,
   * describe, Upload, New group, Delete group, the whole selection toolbar and
   * Delete collection — was offered to everyone and refused by the server,
   * which is how a viewer came to confirm "and its 47 studies. This cannot be
   * undone" and then be told "not found".
   */
  const editable = col.editable === true;
  const cvis = collectionVis(col);
  const total = col.total;
  const pageStats = `${nouns(total, 'study', 'studies')} · ${nouns(col.groups.length, 'group')}`;
  const overItems = (g: StudyGroup | null): OverItem[] =>
    col.studies
      .filter((s) => (g ? s.group_public_id === g.public_id : true))
      .filter((s) => grantsMore(fromServerVisibility(s.visibility_own), cvis))
      .map((s) => ({ name: studyName(s), kind: 'Study', vis: fromServerVisibility(s.visibility_own), mono: true }));
  const empty = col.studies.length === 0 && col.groups.length === 0;

  // ── selection ──
  // Only groups still on the page: a key whose group was deleted elsewhere
  // would otherwise count as a selected group with nothing ticked.
  const selGroupIds = col.groups.filter((g) => selected['g:' + g.public_id]).map((g) => g.public_id);
  const inSelGroup = (s: PlacedStudy) => selGroupIds.includes(s.group_public_id ?? '');
  // A study the selection takes: ticked on its own, or inside a ticked group,
  // which goes whole.
  const isTaken = (s: PlacedStudy) => !!selected[s.public_id] || inSelGroup(s);
  // Every study the selection takes. This is what the toolbar counts, since it
  // is what the person sees ticked and what Delete removes.
  const taken = col.studies.filter(isTaken);
  // The studies the actions send on their own. A ticked group takes its
  // members along itself (moved or deleted as a group), so they are left out
  // here or they would be moved or deleted twice. The toolbar's tally once
  // read this list, and ticking a group made its members vanish from it:
  // "13 studies · 1 group" where fourteen boxes were ticked.
  const selStudies = taken.filter((s) => !inSelGroup(s));
  const selCount = taken.length + selGroupIds.length;
  /** "14 studies · 1 group" (or "and"): the one wording of what is selected,
   *  shared by the toolbar and the Delete confirm so the two cannot disagree. */
  const selectionLabel = (joiner: string) =>
    [taken.length ? nouns(taken.length, 'study', 'studies') : '', selGroupIds.length ? nouns(selGroupIds.length, 'group') : ''].filter(Boolean).join(joiner);
  // Ticking every study in a group is NOT the same as selecting the group.
  // They used to be tied, which made "move these three slides into another
  // group" impossible: the group came along and a group cannot go inside a
  // group, so every target was disabled. The group's own checkbox is now the
  // one thing that selects the group; unticking any of its studies takes the
  // group back out of the selection and leaves the other studies ticked.
  const toggleStudy = (s: PlacedStudy) => {
    const next = { ...selected };
    if (next[s.public_id]) delete next[s.public_id];
    else next[s.public_id] = true;
    if (s.group_public_id) delete next['g:' + s.group_public_id];
    setSelected(next);
  };
  const toggleGroup = (g: StudyGroup, all: PlacedStudy[]) => {
    const next = { ...selected };
    if (next['g:' + g.public_id]) {
      delete next['g:' + g.public_id];
      all.forEach((s) => delete next[s.public_id]);
    } else {
      next['g:' + g.public_id] = true;
      all.forEach((s) => (next[s.public_id] = true));
    }
    setSelected(next);
  };
  const clearSelection = () => setSelected({});
  const selectAll = () => {
    const next: Record<string, true> = {};
    sections.forEach((sec) => {
      // A group goes whole, as its own checkbox takes it: every member is
      // ticked, the ones a filter hides included, since Delete and Move take
      // them all. Ticking only the shown ones left the tally short of what
      // Delete then removed. Studies in no group are the shown ones only.
      (sec.group ? sec.all : sec.shown).forEach((s) => (next[s.public_id] = true));
      if (sec.group) next['g:' + sec.group.public_id] = true;
    });
    setSelected(next);
  };
  // Everything on the page ticked: each shown study and each shown group, by
  // identity. Comparing counts was fooled by a ticked group's hidden members
  // standing in for an unticked shown study.
  const allShownSelected =
    sections.some((sec) => sec.group || sec.shown.length) &&
    sections.every((sec) => sec.shown.every(isTaken) && (!sec.group || selGroupIds.includes(sec.group.public_id)));

  // ── drag and drop ──
  // A ticked study carries every ticked study with it, in page order, and they
  // land together as one block; an unticked one goes alone.
  dropApi.current.carry = (id) => {
    if (!selected[id]) return [id];
    const inOrder = [...col.groups.map((g) => g.public_id), null].flatMap((g) => col.studies.filter((s) => s.group_public_id === g));
    return inOrder.filter((s) => selected[s.public_id]).map((s) => s.public_id);
  };
  dropApi.current.drop = (ids, t) => {
    const v = placement(ids, t, col.studies, matchStudy);
    const next = placeStudies(col.studies, v);
    const band = (list: PlacedStudy[], g: string | null) => list.filter((s) => s.group_public_id === g).map((s) => s.public_id).join(',');
    const touched = new Set([v.groupId, ...col.studies.filter((s) => ids.includes(s.public_id)).map((s) => s.group_public_id)]);
    if ([...touched].every((g) => band(next, g) === band(col.studies, g))) return;
    setSettling(v);
    m.dropStudies.mutate({ id: col.publicId, ...v });
    // A group whose studies left it is no longer wholly selected.
    const left = col.studies.filter((s) => ids.includes(s.public_id) && s.group_public_id && s.group_public_id !== v.groupId);
    if (left.length) setSelected((sel) => { const n = { ...sel }; left.forEach((s) => delete n['g:' + s.group_public_id]); return n; });
    const changedBand = col.studies.some((s) => ids.includes(s.public_id) && s.group_public_id !== v.groupId);
    if (changedBand) {
      const n = ids.length;
      const what = nouns(n, 'study', 'studies');
      toast(v.groupId ? `Moved ${what} to ${col.groups.find((g) => g.public_id === v.groupId)?.name ?? 'the group'}` : `Moved ${what} out of ${n === 1 ? 'its' : 'their'} group`);
    }
  };
  const carried = drag ? col.studies.find((s) => s.public_id === drag.lead) : undefined;

  const movingGroups = selGroupIds.length > 0;
  // Taking studies OUT of a group is a move to this collection with no group —
  // which the list already offered, as a row named after the collection. Nobody
  // read "Breast IHC panel" as "out of the group", so the action says what it
  // does and leads the menu; the collection's own row is then the same click
  // twice and steps aside. Not while a group is ticked: Ungroup moved the lone
  // studies and left the ticked group's members where they were, fewer than
  // the toolbar had counted. Untick the group to ungroup.
  const ungroupable = !movingGroups && selStudies.some((s) => s.group_public_id);
  // Every workspace the person may write, this collection's own first. Its own
  // is found by the collection rather than taken from the header, which may
  // be showing another workspace when the page was opened from a link.
  const writable = targets.data?.workspaces ?? [];
  const homeWorkspace = workspaceOfCollection(writable, col.publicId)?.public_id ?? selectedWorkspace?.public_id;
  const targetSections = orderTargetWorkspaces(writable, homeWorkspace);
  // A workspace with nothing this selection can go into is left out, label and all.
  const bySection = (rows: (c: CollectionTarget) => Target[]): Target[] =>
    targetSections.workspaces.flatMap((w) => {
      const items = w.collections.flatMap(rows);
      if (!items.length) return [];
      return [
        ...(targetSections.labelled ? [{ key: 'ws:' + w.public_id, name: w.name, kind: 'workspace' as const }] : []),
        ...items,
      ];
    });
  // Move stays inside this workspace: slides are COPIED to another, never moved,
  // so nothing of the people here travels with them.
  const here = writable.filter((w) => w.public_id === homeWorkspace);
  // Left out, so that only where this selection can actually go is offered:
  // the collection it is already in while Ungroup is offered (the same click
  // twice) or while a group is ticked (the group would stay and any lone
  // studies would leave their groups — Ungroup by another name); the group it
  // is already in; and every group while a group is selected — a group goes
  // into a collection, not into one.
  const moveTargets: Target[] = [
    ...(ungroupable ? [{ key: UNGROUP, name: 'Ungroup', kind: 'collection' as const, hint: 'no group', divider: true }] : []),
    ...here.flatMap((w) => w.collections).flatMap((c) => [
      ...(c.public_id === col.publicId && (ungroupable || movingGroups)
        ? []
        : [{ key: c.public_id + '/', name: c.name, kind: 'collection' as const }]),
      ...(movingGroups ? [] : c.groups
        .filter((g) => !(c.public_id === col.publicId && selGroupIds.includes(g.public_id)))
        .map((g) => ({ key: c.public_id + '/' + g.public_id, name: g.name, kind: 'group' as const }))),
    ]),
  ];

  const onMove = async (t: Target) => {
    const [colId, grpId] = t.key === UNGROUP ? [col.publicId, ''] : t.key.split('/');
    // Ungroup and this collection's own row are only offered with no group
    // ticked, so everything selected moves, and the groups go whole.
    const n = selStudies.length + selGroupIds.length;
    try {
      if (selStudies.length) await m.moveStudies.mutateAsync({ ids: selStudies.map((s) => s.public_id), collectionId: colId, groupId: grpId || undefined });
      for (const g of selGroupIds) if (colId !== col.publicId) await m.moveGroupToCollection.mutateAsync({ id: col.publicId, groupId: g, target: colId });
      clearSelection();
      toast(t.key === UNGROUP
        ? `Moved ${nouns(n, 'study', 'studies')} out of ${n === 1 ? 'its' : 'their'} group`
        : `Moved ${nouns(n, 'item')} to ${t.name}`);
    } catch {
      /* toasted by the mutation */
    }
  };
  // Copy to: this workspace's collections, and those of the workspaces the
  // person administers (a group may only land in a collection). Inside this
  // workspace the copies share their objects; into another, their images are
  // copied there in the background.
  const dupTargets: Target[] = bySection((c) => [
    { key: c.public_id + '/', name: c.name, kind: 'collection' as const },
    ...(movingGroups ? [] : c.groups.map((g) => ({ key: c.public_id + '/' + g.public_id, name: g.name, kind: 'group' as const }))),
  ]);
  const onDuplicate = async (t: Target) => {
    const [colId, grpId] = t.key.split('/');
    try {
      const r = await m.duplicate.mutateAsync({ studyIds: selStudies.map((s) => s.public_id), groupIds: selGroupIds, collectionId: colId, studyGroupId: grpId || undefined });
      clearSelection();
      toast(duplicatedMessage(r, t.name, workspaceOfCollection(writable, colId)?.name));
    } catch {
      /* toasted by the mutation */
    }
  };
  const onDeleteSelected = async () => {
    // The title counts what the toolbar counts, a ticked group's members
    // included; the body says that they are in there. It used to count them
    // out and name them as extra, so "14 studies · 1 group selected" led to
    // "Delete 13 studies and 1 group?".
    const inGroups = taken.length - selStudies.length;
    const ok = await confirm({
      title: `Delete ${selectionLabel(' and ')}?`,
      body: inGroups
        ? `That includes the ${nouns(inGroups, 'study', 'studies')} inside ${selGroupIds.length === 1 ? 'that group' : 'those groups'}. This cannot be undone.`
        : 'This cannot be undone.',
      action: 'Delete',
      danger: true,
    });
    if (!ok) return;
    try {
      if (selStudies.length) await m.deleteStudies.mutateAsync(selStudies.map((s) => s.public_id));
      for (const g of selGroupIds) await m.deleteGroup.mutateAsync({ id: col.publicId, groupId: g, withStudies: true });
      clearSelection();
      toast('Deleted');
    } catch {
      /* toasted */
    }
  };
  /** One group, from its own Delete button. Destructive, like the collection's. */
  const deleteGroup = async (g: StudyGroup, count: number) => {
    const ok = await confirm({
      title: count ? `Delete group “${g.name}” and its ${nouns(count, 'study', 'studies')}?` : `Delete group “${g.name}”?`,
      body: count ? 'The studies in it are deleted with it. This cannot be undone.' : 'This cannot be undone.',
      action: 'Delete',
      danger: true,
    });
    if (!ok) return;
    try {
      await m.deleteGroup.mutateAsync({ id: col.publicId, groupId: g.public_id, withStudies: true });
      setSelected((sel) => { const n = { ...sel }; delete n['g:' + g.public_id]; return n; });
      toast(count ? 'Group and its studies deleted' : 'Group deleted');
    } catch {
      /* toasted */
    }
  };

  // ── header actions ──
  const commitTitle = async (v: string) => {
    setEditing(null);
    if (!v) {
      if (isNew && empty) {
        await m.remove.mutateAsync(col.publicId).catch(() => undefined);
        nav(`${routeBase()}/collections`);
      }
      return;
    }
    if (v !== col.name) await m.rename.mutateAsync({ id: col.publicId, name: v }).catch(() => undefined);
    if (isNew) {
      const p = new URLSearchParams(params);
      p.delete('new');
      setParams(p, { replace: true });
    }
  };
  // Copy the whole collection into another workspace the person administers
  // (as they do this one): the only way slides reach another workspace.
  const copyTargets = here[0]?.is_admin ? writable.filter((w) => w.public_id !== homeWorkspace && w.is_admin) : [];
  const copyTo = async (target: { public_id: string; name: string }) => {
    setCopyOpen(false);
    try {
      toast(copiedCollectionMessage(await m.copyCollection.mutateAsync({ id: col.publicId, workspace: target.public_id }), target.name));
    } catch {
      /* toasted by the mutation */
    }
  };
  const deleteCollection = async () => {
    const ok = await confirm({ title: `Delete collection “${col.name}” and its ${nouns(col.total, 'study', 'studies')}?`, body: 'This cannot be undone.', action: 'Delete', danger: true });
    if (!ok) return;
    try {
      await m.remove.mutateAsync(col.publicId);
      nav(`${routeBase()}/collections`);
      toast('Collection deleted');
    } catch {
      /* toasted */
    }
  };
  const goDiscussion = () => {
    const el = document.querySelector('[data-disc]');
    if (!el) return;
    window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 100, behavior: 'smooth' });
    setTimeout(() => (el.querySelector('textarea') as HTMLTextAreaElement | null)?.focus({ preventScroll: true }), 450);
  };
  const uploadHere = (groupId?: string) => nav(`${routeBase()}/collections/${col.publicId}/upload${groupId ? `?group=${groupId}` : ''}`);
  const newGroup = async () => {
    try {
      const g = await m.createGroup.mutateAsync({ id: col.publicId, name: 'New group' });
      setNewGroupId(g.public_id);
      setEditing({ kind: 'gname', id: g.public_id });
    } catch {
      /* toasted */
    }
  };

  return (
    <div className="tr-page" style={{ padding: '28px 28px 44px', display: 'flex', flexDirection: 'column', gap: 28, maxWidth: 1440, width: '100%', margin: '0 auto', flex: 1 }}>
      {/* sticky header */}
      <div className="tr-page-bleed tr-wrap-sm" style={{ position: 'sticky', top: 'var(--tr-topbar-h, 56px)', zIndex: 15, background: '#fff', margin: '0 -28px', padding: '8px 28px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 24, borderBottom: `1px solid ${stuck ? '#ebebea' : 'transparent'}` }}>
        <div className="tr-basis-full-sm" style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: 1 }}>
            <InlineTitle value={col.name} readOnly={!editable} editing={editing?.kind === 'title'} onStart={() => setEditing({ kind: 'title' })} onCommit={commitTitle} onCancel={() => setEditing(null)} size={28} placeholder="Collection name" hoverBg="#fff" />
            <VisChip
              vis={cvis}
              readOnly={!editable}
              onChange={(v) => { visWrite.current = m.setVisibility.mutateAsync({ id: col.publicId, vis: v }).catch(() => undefined); }}
              // After any visibility change still on its way: the server only
              // hands out a link for something it already sees as Shared or Public.
              onCopy={() => void visWrite.current.then(() => links.copy('collection', col.publicId, col.name))}
              onReset={() => void links.reset('collection', col.publicId, col.name)}
            />
            <OverVisibleWarning items={overItems(null)} container="collection" containerVis={cvis} />
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 'none' }}>
          {editable ? (
            <Button variant="primary" size={32} icon={<IconUpload size={14} />} onClick={() => uploadHere()}>
              Upload
            </Button>
          ) : null}
          <Button variant="quiet" size={32} title={col.liked ? 'Remove from saved' : 'Save'} icon={<IconHeart size={14} fill={col.liked ? '#d9342b' : 'none'} color={col.liked ? '#d9342b' : 'currentColor'} />} onClick={() => m.like.mutate({ kind: 'collection', id: col.publicId, liked: col.liked })}>
            {col.like_count}
          </Button>
          <Button variant="quiet" size={32} icon={<IconComment size={14} />} onClick={goDiscussion}>
            Comment
          </Button>
          {/* What is done TO the collection rather than in it — copying it into
              another workspace, deleting it — sits under one Options menu, so
              the row keeps Upload as its one black button and no rare, heavy
              action stands permanently in it. Copy appears only where it can go. */}
          {/* Nothing under Options is a read, so a viewer is not shown the
              button at all rather than an empty menu. */}
          {editable ? (
          <MenuAnchor display="flex">
            <Button variant="quiet" size={32} iconRight={<IconChevronDown size={14} />} title="What can be done with this collection" onClick={(e) => { e.stopPropagation(); setCopyOpen(!copyOpen); }}>
              Options
            </Button>
            {copyOpen ? (
              <MenuSurface width={260} right={0} shadow="menu" maxHeight={360}>
                {copyTargets.length ? (
                  <>
                    <MenuLabel>COPY TO</MenuLabel>
                    {copyTargets.map((w) => (
                      <MenuItem key={w.public_id} icon={<IconCopy size={14} />} onClick={(e) => { e.stopPropagation(); void copyTo(w); }}>{w.name}</MenuItem>
                    ))}
                    <div style={{ height: 1, background: '#ebebea', margin: '4px 6px' }} />
                  </>
                ) : null}
                <MenuItem danger icon={<IconTrash size={14} />} onClick={(e) => { e.stopPropagation(); setCopyOpen(false); void deleteCollection(); }}>
                  Delete collection
                </MenuItem>
              </MenuSurface>
            ) : null}
          </MenuAnchor>
          ) : null}
        </div>
      </div>

      {/* Its own height, never the page's leftover: in the page's column, flex: 1 stretched it down a tall window when the collection was short. */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0, marginTop: -22 }}>
        <div style={{ width: '100%' }}>
          <RichText html={col.description ?? ''} editable={editable} startEditing={aboutEditing} placeholder="Add a description" onChange={(html) => { setAboutEditing(false); m.describe.mutate({ id: col.publicId, description: html }); }} onCancel={() => setAboutEditing(false)} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, fontSize: 13, color: '#6b6b70' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <Avatar name={col.author ?? ''} size={22} />
            <span style={{ color: '#0f0f10', fontWeight: 500 }}>{col.author ?? ''}</span>
          </div>
          <span>·</span>
          <span>{pageStats}</span>
        </div>
      </div>

      <div>
        <BrowseBar
          allCount={col.groups.length + col.studies.filter((s) => s.group_public_id === null).length}
          savedCount={col.studies.filter((s) => s.group_public_id === null && s.liked).length}
          state={browse}
          placeholder="Filter groups and studies"
          onChange={setBrowse}
        />
      </div>

      {anyFilter && sections.length === 0 ? (
        <div style={{ padding: '22px 16px', border: '1px solid #ebebea', borderRadius: 10, fontSize: 13, color: '#8a8a8f', textAlign: 'center' }}>
          {browse.query.trim() ? `No studies match “${browse.query.trim()}”` : browse.tab === 'saved' ? 'No saved studies' : `No ${VIS_LABEL[browse.share as Visibility]?.toLowerCase()} studies`}
        </div>
      ) : null}

      <div ref={bandsRef} style={{ display: 'contents' }}>
      {sections.map((sec, idx) => {
        const g = sec.group;
        const isGroup = !!g;
        const gIndex = g ? col.groups.findIndex((x) => x.public_id === g.public_id) : -1;
        const canUp = gIndex > 0;
        const canDown = isGroup && gIndex < col.groups.length - 1;
        const selN = sec.all.filter((s) => selected[s.public_id]).length;
        const allSel = isGroup && !!selected['g:' + g!.public_id];
        const someSel = isGroup && selN > 0 && !allSel;
        const countLabel = anyFilter ? `${sec.shown.length} of ${sec.all.length} studies` : nouns(sec.all.length, 'study', 'studies');
        const editingName = isGroup && editing?.kind === 'gname' && editing.id === g!.public_id;
        const editingDesc = isGroup && editing?.kind === 'gdesc' && editing.id === g!.public_id;
        const commitName = async (v: string) => {
          setEditing(null);
          if (!g) return;
          if (!v) {
            if (newGroupId === g.public_id && sec.all.length === 0) await m.deleteGroup.mutateAsync({ id: col.publicId, groupId: g.public_id }).catch(() => undefined);
            return;
          }
          setNewGroupId(null);
          if (v !== g.name) m.renameGroup.mutate({ id: col.publicId, groupId: g.public_id, name: v });
        };
        return (
          <div
            key={sec.key}
            data-grp={sec.key}
            data-band={sec.key}
            data-layout={browse.view === 'grid' ? 'grid' : 'list'}
            style={{
              display: 'flex', flexDirection: 'column', gap: 16, padding: isGroup ? '18px 20px 20px' : 0, border: `1px solid ${isGroup ? '#ebebea' : 'transparent'}`, borderRadius: 12, background: isGroup ? '#fafaf9' : 'transparent', scrollMarginTop: 120,
              // The band a drop would land in is outlined while the pointer is over it.
              outline: drag?.target?.band === sec.key ? '2px solid #0f0f10' : '2px solid transparent',
              outlineOffset: isGroup ? -1 : 10,
              transition: 'outline-color 120ms',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0, flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                  {isGroup && editable ? <Checkbox checked={allSel} indeterminate={someSel} onToggle={() => toggleGroup(g!, sec.all)} size={16} title="Select this group and everything in it" /> : null}
                  {isGroup ? <span style={{ fontSize: 12, fontWeight: 600, color: '#8a8a8f', fontFamily: "'JetBrains Mono', monospace", flex: 'none', minWidth: 18 }}>{String(gIndex + 1).padStart(2, '0')}</span> : null}
                  {isGroup ? (
                    <InlineTitle value={g!.name} readOnly={!editable} editing={editingName} onStart={() => setEditing({ kind: 'gname', id: g!.public_id })} onCommit={commitName} onCancel={() => setEditing(null)} size={16} as="h2" placeholder="Group name" hoverBg="#fff" />
                  ) : (
                    // "Ungrouped studies" only once groups exist: in a
                    // collection with no groups there is nothing to be
                    // ungrouped from, and the word is just noise.
                    <h2 style={{ margin: '0 -7px', padding: '1px 6px', borderRadius: 5, fontSize: 16, fontWeight: 600, cursor: 'default' }}>{col.groups.length ? 'Ungrouped studies' : 'Studies'}</h2>
                  )}
                  {isGroup ? <OverVisibleWarning items={overItems(g)} container="group" containerVis={cvis} /> : null}
                </div>
                {isGroup ? (
                  <InlineText value={g!.description ?? ''} readOnly={!editable} editing={editingDesc} onStart={() => setEditing({ kind: 'gdesc', id: g!.public_id })} onCommit={(v) => { setEditing(null); if (v !== (g!.description ?? '')) m.describeGroup.mutate({ id: col.publicId, groupId: g!.public_id, description: v }); }} onCancel={() => setEditing(null)} />
                ) : null}
                {/* An empty band says so in its body; a "0 studies" line above
                    that says it twice. */}
                {sec.all.length ? <span style={{ fontSize: 12.5, color: '#8a8a8f' }}>{countLabel}</span> : null}
              </div>
              {/* One family of buttons per band: same height, same radius, each
                  one quiet until the cursor reaches it. Upload was a ghost, the
                  reorder pair was two bare chevrons and Delete was red text —
                  three different things in a row of three controls. */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, flex: 'none' }}>
                {editable ? (
                <>
                <Button variant="quiet" size={30} px={10} title={isGroup ? `Upload to ${g!.name}` : 'Upload a study'} icon={<IconUpload size={14} />} onClick={() => uploadHere(g?.public_id)}>
                  Upload
                </Button>
                {!isGroup ? (
                  <Button variant="quiet" size={30} px={10} icon={<IconPlus size={13} />} onClick={newGroup}>
                    New group
                  </Button>
                ) : (
                  <>
                    {/* The reorder pair: one shell, split by a hairline, that
                        fills under the cursor like the buttons beside it — two
                        buttons, not decoration on the band. */}
                    <div className="tr-hover-quiet" style={{ display: 'flex', height: 30, borderRadius: 7, overflow: 'hidden', background: 'transparent' }}>
                      <button type="button" title={`Move “${g!.name}” up`} aria-label={`Move group ${g!.name} up`} disabled={!canUp} className={canUp ? 'tr-hover-line' : undefined} onClick={() => canUp && m.moveGroup.mutate({ id: col.publicId, groupId: g!.public_id, beforeId: col.groups[gIndex - 1].public_id })} style={{ width: 32, display: 'grid', placeItems: 'center', border: 0, background: 'transparent', color: canUp ? '#0f0f10' : '#d4d4d2', cursor: canUp ? 'pointer' : 'default' }}>
                        <IconChevronUp size={15} sw={2.4} />
                      </button>
                      <div style={{ width: 1, background: '#ebebea' }} />
                      <button type="button" title={`Move “${g!.name}” down`} aria-label={`Move group ${g!.name} down`} disabled={!canDown} className={canDown ? 'tr-hover-line' : undefined} onClick={() => canDown && m.moveGroup.mutate({ id: col.publicId, groupId: g!.public_id, afterId: col.groups[gIndex + 1].public_id })} style={{ width: 32, display: 'grid', placeItems: 'center', border: 0, background: 'transparent', color: canDown ? '#0f0f10' : '#d4d4d2', cursor: canDown ? 'pointer' : 'default' }}>
                        <IconChevronDown size={15} sw={2.4} />
                      </button>
                    </div>
                    <Button variant="quiet" className="tr-hover-quiet-danger" size={30} px={10} title={`Delete “${g!.name}” and its studies`} icon={<IconTrash size={14} />} onClick={() => deleteGroup(g!, sec.all.length)}>
                      Delete
                    </Button>
                  </>
                )}
                </>
                ) : null}
              </div>
            </div>
            {sec.all.length === 0 ? (
              // Plain text, not a dashed box: a box that looked like a dropzone
              // confused people. A study dragged here outlines the whole band.
              empty ? (
                <div style={{ padding: '36px 16px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, textAlign: 'center' }}>
                  <span style={{ fontSize: 15, fontWeight: 500 }}>This collection is empty</span>
                  {/* A viewer is told it is empty and not invited to fill it:
                      the upload page would refuse them a target. */}
                  {editable ? (
                    <span style={{ fontSize: 13.5, color: '#6b6b70' }}>
                      <TextLink onClick={() => uploadHere()} fontSize={13.5} underline>Upload a study</TextLink> to get started.
                    </span>
                  ) : null}
                </div>
              ) : (
                <div style={{ fontSize: 13, color: '#8a8a8f' }}>
                  {isGroup ? 'No studies in this group yet.' : 'No studies outside groups yet.'}
                  {editable ? <> <TextLink onClick={() => uploadHere(g?.public_id)} fontSize={13} underline>Upload a study</TextLink></> : null}
                </div>
              )
            ) : browse.view === 'grid' ? (
              <div className="tr-cols-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '24px 20px' }}>
                {sec.shown.map((s) => (
                  <div
                    key={s.public_id}
                    data-sid={s.public_id}
                    data-ghost={drag?.ids.includes(s.public_id) ? '' : undefined}
                    onPointerDown={editable ? onPointerDown(s.public_id) : undefined}
                    style={{ minWidth: 0, opacity: drag?.ids.includes(s.public_id) ? 0.3 : 1, WebkitTouchCallout: 'none' }}
                  >
                  <StudyCard
                    cover={studyThumbnail(s.public_id)}
                    count={s.images}
                    vis={studyVis(s)}
                    name={studyName(s)}
                    desc={s.description ?? ''}
                    diagnosis={s.diagnosis ?? ''}
                    meta={shortDate(s.created_utc)}
                    comments={s.comment_count}
                    likes={s.like_count}
                    liked={s.liked}
                    selected={!!selected[s.public_id]}
                    onToggle={editable ? () => toggleStudy(s) : undefined}
                    onOpen={() => openStudy(s.public_id)}
                    onToggleLike={() => m.like.mutate({ kind: 'study', id: s.public_id, liked: s.liked })}
                  />
                  </div>
                ))}
              </div>
            ) : (
              <div className="tr-table-1040" style={{ border: '1px solid #ebebea', borderRadius: 10, overflowX: 'auto' }}>
                <div style={{ display: 'grid', gridTemplateColumns: LIST_COLUMNS, alignItems: 'center', height: 40, padding: '0 16px', background: '#fafaf9', borderBottom: '1px solid #ebebea', fontSize: 12, fontWeight: 500, color: '#6b6b70', gap: 16 }}>
                  <div /><div /><div>Study</div><div>Description</div><div>Diagnosis</div><div>Images</div><div>Visibility</div><div>Uploaded</div><div>Saves</div>
                </div>
                {sec.shown.map((s) => {
                  const v = studyVis(s);
                  const sel = !!selected[s.public_id];
                  return (
                    <div
                      key={s.public_id}
                      data-sid={s.public_id}
                      data-ghost={drag?.ids.includes(s.public_id) ? '' : undefined}
                      data-drag-width="300"
                      className="tr-hover-soft-fg"
                      onClick={() => openStudy(s.public_id)}
                      onPointerDown={editable ? onPointerDown(s.public_id) : undefined}
                      style={{ display: 'grid', gridTemplateColumns: LIST_COLUMNS, alignItems: 'center', padding: '10px 16px', borderBottom: '1px solid #f0f0ee', gap: 16, cursor: 'pointer', background: sel ? '#fafaf9' : '#fff', position: 'relative', opacity: drag?.ids.includes(s.public_id) ? 0.3 : 1, WebkitTouchCallout: 'none' }}
                    >
                      {editable ? <Checkbox checked={sel} onToggle={() => toggleStudy(s)} label={`Select ${studyName(s)}`} /> : <div />}
                      <StackMini cover={studyThumbnail(s.public_id)} />
                      {/* Two lines at most, like the columns beside it: a study with no Study
                          ID is named by its description, which a narrow column wraps. */}
                      <div className="tr-clamp-2" style={{ fontSize: 14, fontWeight: 500, fontFamily: "'JetBrains Mono', monospace", lineHeight: 1.4, overflowWrap: 'anywhere' }}>{studyName(s)}</div>
                      <div className="tr-clamp-2" style={{ fontSize: 13, color: '#6b6b70', lineHeight: 1.4 }}>{s.description ?? ''}</div>
                      <div className="tr-clamp-2" style={{ fontSize: 13, fontWeight: 500, color: '#0f0f10', lineHeight: 1.4 }}>{s.diagnosis ? <><DxBadge />{s.diagnosis}</> : null}</div>
                      <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12.5 }}>{s.images}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#6b6b70' }}><VisIcon vis={v} />{VIS_LABEL[v]}</div>
                      <div style={{ fontSize: 13, color: '#6b6b70' }}>{shortDate(s.created_utc)}</div>
                      <div onClick={(e) => { e.stopPropagation(); m.like.mutate({ kind: 'study', id: s.public_id, liked: s.liked }); }} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12.5, color: '#6b6b70' }}>
                        <IconHeart size={13} fill={s.liked ? '#d9342b' : 'none'} color={s.liked ? '#d9342b' : 'currentColor'} />
                        {s.like_count}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {idx === sections.length - 1 ? null : null}
          </div>
        );
      })}
      </div>

      <div data-disc="1" style={{ display: 'flex', flexDirection: 'column', gap: 18, paddingTop: 28, borderTop: '1px solid #ebebea', maxWidth: 760 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Discussion</h2>
          <span style={{ fontSize: 13, color: '#8a8a8f' }}>{col.comment_count}</span>
        </div>
        <CommentThread
          comments={toThread(thread.data?.comments ?? [])}
          me={{ name: meName }}
          canPost
          placeholder="Comment on this collection"
          emptyText="No comments yet. Start the discussion."
          onPost={(body) => cm.post.mutate({ body })}
          onReply={(parentId, body) => cm.post.mutate({ body, parentId })}
          onEdit={(commentId, body) => cm.edit.mutate({ commentId, body })}
          onDelete={async (commentId) => { if (await confirm({ title: 'Delete this comment?', action: 'Delete', danger: true })) cm.remove.mutate(commentId); }}
        />
      </div>

      {drag && carried ? <DragCard ref={overlayRef} study={carried} count={drag.ids.length} x={drag.x - drag.offsetX} y={drag.y - drag.offsetY} width={drag.width} row={browse.view !== 'grid'} /> : null}

      {editable && selCount > 0 ? (
        <SelectionToolbar
          label={selectionLabel(' · ') + ' selected'}
          onSelectAll={selectAll}
          allSelected={allShownSelected}
          moveTargets={moveTargets}
          moveNote={movingGroups ? "Groups can't be placed inside other groups. Pick a collection." : undefined}
          onMove={onMove}
          dupTargets={dupTargets}
          onDuplicate={onDuplicate}
          onDelete={onDeleteSelected}
          onClear={clearSelection}
        />
      ) : null}
    </div>
  );
}

/**
 * What follows the pointer during a drag: the grabbed study's image (a small
 * row card in the list view), with the others it carries stacked behind it
 * and their number on a badge.
 */
const DragCard = forwardRef<HTMLDivElement, { study: PlacedStudy; count: number; x: number; y: number; width: number; row: boolean }>(function DragCard({ study, count, x, y, width, row }, ref) {
  const lift = '0 18px 40px rgba(15,15,16,.22), 0 2px 8px rgba(15,15,16,.12)';
  const badge = count > 1 ? (
    <div style={{ position: 'absolute', top: -9, right: -9, minWidth: 24, height: 24, padding: '0 7px', borderRadius: 12, background: '#0f0f10', color: '#fff', fontSize: 12.5, fontWeight: 600, display: 'grid', placeItems: 'center', boxShadow: '0 0 0 2px #fff' }}>{count}</div>
  ) : null;
  const behind = (n: number, radius: number) =>
    Array.from({ length: Math.min(count - 1, 2) }, (_, i) => (
      <div key={i} style={{ position: 'absolute', inset: 0, transform: `translate(${(i + 1) * n}px, ${(i + 1) * n}px) rotate(${(i + 1) * 2}deg)`, borderRadius: radius, background: i ? '#ececea' : '#dcdcd9', border: '1px solid #d4d4d2' }} />
    )).reverse();
  return (
    <div ref={ref} style={{ position: 'fixed', left: x, top: y, width, zIndex: 1000, pointerEvents: 'none', transform: 'rotate(-1.5deg)' }}>
      {row ? (
        <div style={{ position: 'relative' }}>
          {behind(4, 10)}
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 12, padding: '8px 14px 8px 8px', background: '#fff', border: '1px solid #ebebea', borderRadius: 10, boxShadow: lift }}>
            <Cover src={studyThumbnail(study.public_id)} fallback="#e8e8e6" radius={6} style={{ width: 64, height: 44, flex: 'none', border: '1px solid #ebebea' }} />
            <span className="tr-ellipsis" style={{ fontSize: 14, fontWeight: 500, fontFamily: "'JetBrains Mono', monospace" }}>{studyName(study)}</span>
          </div>
          {badge}
        </div>
      ) : (
        <div style={{ position: 'relative', aspectRatio: '4 / 3' }}>
          {behind(6, 10)}
          <Cover src={studyThumbnail(study.public_id)} fallback="#e8e8e6" radius={10} style={{ position: 'absolute', inset: 0, border: '1px solid #ebebea', boxShadow: lift }} />
          {badge}
        </div>
      )}
    </div>
  );
});

export type { CollectionDetail };
